import { ref, watch, onScopeDispose, type Ref } from 'vue';
import type { QuoteByTypeResult } from '~~/server/services/quote-by-type';
import { VALID_APARTMENT_TYPES, type ApartmentTypeId } from '~~/server/db/apartment-types';

function diffDaysBetween(isoA: string, isoB: string): number {
  const a = new Date(isoA + 'T00:00:00Z').getTime();
  const b = new Date(isoB + 'T00:00:00Z').getTime();
  return Math.round((b - a) / 86_400_000);
}

export type QuotesByType = Record<ApartmentTypeId, QuoteByTypeResult>;

type Args = {
  checkin: Ref<string>;
  checkout: Ref<string>;
  guests: Ref<number>;
};

export function useQuoteByType(args: Args, debounceMs = 300) {
  const quotes = ref<QuotesByType | null>(null);
  const pending = ref(false);
  const error = ref<Error | null>(null);

  let timer: ReturnType<typeof setTimeout> | null = null;

  const fetchAll = async () => {
    if (!args.checkin.value || !args.checkout.value || args.guests.value <= 0) {
      quotes.value = null;
      return;
    }
    const nights = diffDaysBetween(args.checkin.value, args.checkout.value);
    if (nights >= 30) {
      // Long-stay: frontend swaps in LongStayContact instead; don't call API.
      quotes.value = null;
      return;
    }
    pending.value = true;
    error.value = null;
    try {
      const results = await Promise.all(
        VALID_APARTMENT_TYPES.map((type) =>
          $fetch<QuoteByTypeResult>('/api/quote-by-type', {
            method: 'POST',
            body: {
              type,
              checkin: args.checkin.value,
              checkout: args.checkout.value,
              guests: args.guests.value,
            },
          }),
        ),
      );
      const out = {} as QuotesByType;
      VALID_APARTMENT_TYPES.forEach((type, i) => {
        out[type] = results[i]!;
      });
      quotes.value = out;
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e));
      quotes.value = null;
    } finally {
      pending.value = false;
    }
  };

  watch(
    [args.checkin, args.checkout, args.guests],
    () => {
      if (timer) clearTimeout(timer);
      // Clear stale results immediately so UI shows skeleton not stale prices.
      quotes.value = null;
      timer = setTimeout(fetchAll, debounceMs);
    },
    { immediate: true },
  );

  onScopeDispose(() => {
    if (timer) clearTimeout(timer);
  });

  return { quotes, pending, error, refresh: fetchAll };
}
